import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';

import { UserRoleSet } from '@paragon/contract-library';
import { UserRoleEditor } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';

import { createUser } from '../../actions/userActions';
import { getOrganizationsSorted } from '../../selectors/organizations';
import { RootState } from '../../store';
import { UserProperties } from '../personal/UserProperties';
import { getAreAnyErrorsForDraftUser, validate } from './validate';

export interface DraftUserRecord {
  readonly organization: string | null;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly location: string;
  readonly password: string;
  readonly roleSet: UserRoleSet;
}

interface ValidatedUserRecord extends DraftUserRecord {
  readonly organization: string;
}

const NULL_USER = {
  organization: null,
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  location: 'No Location',
  password: '',
  roleSet: { role: null, whitelist: [], blacklist: [] },
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  readonly showErrors: boolean;
  readonly userRecord: DraftUserRecord;
}

class CreateUser extends React.Component<Props, State> {
  state: State = {
    showErrors: false,
    userRecord: {
      ...NULL_USER,
      location:
        this.props.organizations[0] &&
        this.props.organizations[0].locations &&
        this.props.organizations[0].locations.length &&
        this.props.organizations[0].locations.length > 0
          ? this.props.organizations[0].locations[0]
          : 'No Location',
    },
  };

  onUserChange = (record: DraftUserRecord) => {
    this.setState((prevState) => {
      const userRecord = { ...prevState.userRecord, ...record };
      return { userRecord };
    });
  };

  onUserRoleSetChange = (value: UserRoleSet) => {
    this.setState((prevState) => ({ userRecord: { ...prevState.userRecord, roleSet: value } }));
  };

  onCreate = () => {
    if (this.validate(this.state.userRecord)) {
      this.props.createUser(this.state.userRecord);
      this.setState({
        userRecord: NULL_USER,
        showErrors: false,
      });
    }
  };

  validate(userRecord: DraftUserRecord): userRecord is ValidatedUserRecord {
    const areAnyErrors = getAreAnyErrorsForDraftUser(userRecord);

    if (areAnyErrors) {
      this.setState({ showErrors: true });
      this.props.enqueueError('Please fill all required fields');
      return false;
    }

    return true;
  }

  render() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '25%', p: 1 }}>
        <Typography variant="h6">Create User</Typography>
        <Divider sx={{ mb: 4 }} />
        <Paper sx={{ p: 2, pt: 0, bgcolor: 'backContrast.main' }} elevation={1}>
          <UserProperties
            user={this.state.userRecord}
            organizations={this.props.organizations}
            isCreating
            onChange={this.onUserChange}
            validate={validate}
            showErrors={this.state.showErrors}
          />
          <UserRoleEditor value={this.state.userRecord.roleSet} onChange={this.onUserRoleSetChange} />
        </Paper>
        <Button
          sx={{
            mt: 3,
            mb: 1,
            py: 1,
            width: 150,
            color: 'buttonColor.main',
            bgcolor: 'backContrast.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              color: '#fff',
            },
          }}
          variant="contained"
          onClick={this.onCreate}
        >
          Create
        </Button>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  organizations: getOrganizationsSorted(state),
});

const actionCreators = {
  createUser,
  enqueueError,
};

export default connect(mapStateToProps, actionCreators)(CreateUser);
